/*
 * Pick Your Flavours box builder (sections/bundle-builder.liquid).
 * Customers tap flavours into a box; once it's full the real packs go to the
 * cart in one request and the Shopify automatic discount takes the saving off.
 */
if (!customElements.get('bundle-builder')) {
  customElements.define('bundle-builder', class BundleBuilder extends HTMLElement {
    connectedCallback() {
      this.size = Number(this.dataset.boxSize) || 6;
      this.discount = Number(this.dataset.discount) || 0;
      this.picks = [];
      this.cards = [...this.querySelectorAll('.bb-card')];
      this.slots = [...this.querySelectorAll('.bb-slot')];
      this.submitButton = this.querySelector('[data-submit]');
      this.submitLabel = this.submitButton.textContent.trim();
      this.currency = new Intl.NumberFormat(document.documentElement.lang || 'en-AU', {
        style: 'currency',
        currency: (window.Shopify && Shopify.currency && Shopify.currency.active) || 'AUD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });

      this.cards.forEach((card) => {
        card.querySelector('[data-add]').addEventListener('click', () => this.add(card));
        card.querySelector('[data-minus]').addEventListener('click', () => this.remove(card));
      });
      this.submitButton.addEventListener('click', () => this.submit());
      this.render();
    }

    money(cents) {
      return this.currency.format(cents / 100);
    }

    count(card) {
      return this.picks.filter((c) => c === card).length;
    }

    add(card) {
      if (this.picks.length >= this.size || this.count(card) >= Number(card.dataset.max)) return;
      this.picks.push(card);
      this.render();
      if (this.picks.length === this.size) this.celebrate();
    }

    remove(card) {
      const i = this.picks.lastIndexOf(card);
      if (i > -1) this.picks.splice(i, 1);
      this.render();
    }

    render() {
      const n = this.picks.length;
      const left = this.size - n;
      const full = n === this.size;
      const value = this.picks.reduce((sum, c) => sum + Number(c.dataset.price), 0);
      const saving = this.money(this.discount);

      this.cards.forEach((card) => {
        const q = this.count(card);
        const max = Number(card.dataset.max);
        const add = card.querySelector('[data-add]');
        card.classList.toggle('is-on', q > 0);
        card.querySelector('[data-qty]').textContent = q;
        card.querySelector('[data-minus]').hidden = q === 0;
        if (max === 0) return;
        add.disabled = full || q >= max;
        add.textContent = q >= max && !full ? 'No more left' : q > 0 ? 'Add another' : 'Add';
      });

      this.slots.forEach((slot, i) => {
        const card = this.picks[i];
        slot.classList.toggle('is-full', Boolean(card));
        slot.style.backgroundImage = card && card.dataset.thumb ? `url("${card.dataset.thumb}")` : '';
        slot.title = card ? card.dataset.name : '';
      });

      this.querySelector('[data-count]').textContent = n;
      this.querySelector('[data-fill]').style.width = `${(n / this.size) * 100}%`;

      const meter = this.querySelector('[data-meter-msg]');
      const msg = this.querySelector('[data-bar-msg]');
      const sub = this.querySelector('[data-bar-sub]');
      if (n === 0) {
        meter.innerHTML = `Pick ${this.size} to unlock <b>${saving} off + free shipping</b>`;
        msg.innerHTML = `Pick ${this.size} flavours to save <b>${saving}</b>`;
        sub.textContent = 'Doubles allowed · free shipping';
      } else if (!full) {
        meter.innerHTML = `<b>${left} more</b> to unlock ${saving} off + free shipping`;
        msg.innerHTML = `${left} more to go! Then you save <b>${saving}</b>`;
        sub.textContent = `${n} picked so far`;
      } else {
        meter.innerHTML = `<b>Yay! You saved ${saving} and shipping is free.</b>`;
        msg.innerHTML = `Yay! You saved <b>${saving}</b> + free shipping`;
        sub.textContent = `${this.size} packs worth ${this.money(value)} · you pay ${this.money(value - this.discount)}`;
      }

      this.submitButton.disabled = !full;
      this.querySelector('[data-bar]').classList.toggle('is-done', full);
    }

    async submit() {
      if (this.picks.length !== this.size || this.submitButton.disabled) return;

      const quantities = new Map();
      this.picks.forEach((c) => quantities.set(c.dataset.variant, (quantities.get(c.dataset.variant) || 0) + 1));
      const items = [...quantities].map(([id, quantity]) => ({
        id: Number(id),
        quantity,
        properties: { _bundle: 'Pick Your Flavours' },
      }));

      this.showError('');
      this.submitButton.disabled = true;
      this.submitButton.textContent = 'Adding…';

      try {
        const response = await fetch(`${(window.routes && routes.cart_add_url) || '/cart/add'}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items }),
        });
        const data = await response.json();
        if (!response.ok || data.status) throw new Error(data.description || data.message || 'Something went wrong.');

        this.submitButton.textContent = 'Added!';
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        if (this.dataset.afterAdd === 'cart') {
          window.location.href = (window.routes && routes.cart_url) || '/cart';
          return;
        }
        setTimeout(() => {
          this.picks = [];
          this.submitButton.textContent = this.submitLabel;
          this.render();
        }, 1500);
      } catch (error) {
        // Most likely someone else just bought the last of a flavour.
        this.showError(`${error.message} Try swapping a flavour, or refresh to see what's in stock.`);
        this.submitButton.textContent = this.submitLabel;
        this.submitButton.disabled = false;
      }
    }

    showError(text) {
      const el = this.querySelector('[data-error]');
      el.textContent = text;
      el.hidden = !text;
    }

    celebrate() {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      const colours = ['--bb-pink', '--bb-lemon', '--bb-deep', '--bb-purple'].map((v) =>
        getComputedStyle(this).getPropertyValue(v).trim()
      );
      for (let i = 0; i < 60; i++) {
        const bit = document.createElement('span');
        bit.className = 'bb-confetti';
        bit.style.left = `${Math.random() * 100}vw`;
        bit.style.background = colours[i % colours.length];
        bit.style.animationDelay = `${Math.random() * 0.4}s`;
        bit.style.animationDuration = `${1.2 + Math.random()}s`;
        document.body.appendChild(bit);
        setTimeout(() => bit.remove(), 2800);
      }
    }
  });
}
